import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import IconButton from "@material-ui/core/IconButton";

import MenuBookIcon from "@material-ui/icons/MenuBook";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ImageIcon from "@material-ui/icons/Image";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import SearchField from "designV2/components/SearchField";
import EmptyState from "designV2/components/EmptyState";
import { Skeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { assetUrl, readableError } from "../shopFields";
import "../superShops.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const GET_SHOP = gql`
  query getShopMenuForEdit($id: ID!) {
    getShopByAdmin(id: $id) {
      _id
      name
      rootCategory {
        _id
        title
      }
      shopMenu {
        _id
        subMenus {
          _id
          name
          products {
            _id
            title
          }
        }
      }
    }
  }
`;

const GET_PRODUCTS = gql`
  query getProductsForMenu($shop: ID) {
    getProductsByAdmin(filters: { shop: $shop }) {
      _id
      title
      price
      stock
      photoUrl
    }
  }
`;

const CREATE_MENU = gql`
  mutation createShopMenuByAdmin($shopId: ID!, $subMenus: [SubMenuInput]!) {
    createShopMenuByAdmin(inputs: { shopId: $shopId, subMenus: $subMenus }) {
      _id
    }
  }
`;

const UPDATE_MENU = gql`
  mutation updateShopMenuByAdmin($_id: ID!, $subMenus: [SubMenuInput]) {
    updateShopMenuByAdmin(_id: $_id, inputs: { subMenus: $subMenus }) {
      _id
    }
  }
`;

/**
 * SuperAddShopMenu - the shop's menu: a list of groupings, each holding
 * some of the shop's products.
 *
 * The whole menu is saved in one mutation, so this screen holds the draft
 * locally and only writes when you press Save. That is what the previous
 * version did too, but it never said so — there was no indication that
 * renaming a group or moving a product had not been saved yet. The
 * unsaved state is now explicit, and leaving with changes pending warns.
 */
const SuperAddShopMenu = ({ id: shopId, shopName, shopTitle, wording }) => {
  const words = wording || { menu: "Categories", menuItem: "Category", products: "Products" };

  const [groups, setGroups] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionResult, setActionResult] = useState("");

  const [nameTarget, setNameTarget] = useState(null); // { index, value } or { index: null } to add
  const [nameValue, setNameValue] = useState("");
  const [nameError, setNameError] = useState("");
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [productsTarget, setProductsTarget] = useState(null);
  const [productSearch, setProductSearch] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_SHOP, {
    variables: { id: shopId },
    fetchPolicy: "network-only",
  });
  const { data: productData } = useQuery(GET_PRODUCTS, {
    variables: { shop: shopId },
    errorPolicy: "all",
  });

  const [createMenu] = useMutation(CREATE_MENU);
  const [updateMenu] = useMutation(UPDATE_MENU);

  const shop = data && data.getShopByAdmin;
  const shopMenu = shop && shop.shopMenu;
  const allProducts = (productData && productData.getProductsByAdmin) || [];

  // Seed the local draft from the server once.
  useEffect(() => {
    if (!shopMenu) {
      setGroups([]);
      return;
    }
    setGroups(
      (shopMenu.subMenus || []).map((subMenu) => ({
        name: subMenu.name,
        productIds: (subMenu.products || []).map((p) => p._id),
      }))
    );
    setDirty(false);
  }, [shopMenu]);

  // A half-edited menu that is never saved is silently lost, so leaving
  // the page with pending changes asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const mutate = (updater) => {
    setGroups(updater);
    setDirty(true);
    setActionResult("");
  };

  /* ---------------- group editing ---------------- */

  const openAdd = () => {
    setNameTarget({ index: null });
    setNameValue("");
    setNameError("");
  };

  const openRename = (index) => {
    setNameTarget({ index });
    setNameValue(groups[index].name);
    setNameError("");
  };

  const saveName = () => {
    const value = nameValue.trim();
    if (!value) {
      setNameError(`A ${words.menuItem.toLowerCase()} needs a name.`);
      return;
    }
    const clash = groups.some(
      (group, index) =>
        group.name.toLowerCase() === value.toLowerCase() &&
        index !== (nameTarget.index === null ? -1 : nameTarget.index)
    );
    if (clash) {
      setNameError("Another one already has that name.");
      return;
    }
    if (nameTarget.index === null) {
      mutate((current) => current.concat({ name: value, productIds: [] }));
    } else {
      mutate((current) =>
        current.map((group, index) =>
          index === nameTarget.index ? { ...group, name: value } : group
        )
      );
    }
    setNameTarget(null);
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= groups.length) return;
    mutate((current) => {
      const next = current.slice();
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const removeGroup = () => {
    mutate((current) => current.filter((_, index) => index !== deleteIndex));
    setDeleteIndex(null);
  };

  const toggleProduct = (groupIndex, productId) => {
    mutate((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              productIds: group.productIds.includes(productId)
                ? group.productIds.filter((id) => id !== productId)
                : group.productIds.concat(productId),
            }
          : group
      )
    );
  };

  /* ---------------- save ---------------- */

  const onSave = () => {
    const named = groups.filter((group) => group.name.trim());
    if (!named.length) {
      setActionError(`Add at least one ${words.menuItem.toLowerCase()} before saving.`);
      return;
    }
    setSaving(true);
    setActionError("");
    setActionResult("");

    const subMenus = named.map((group) => ({ name: group.name, products: group.productIds }));
    const run = shopMenu
      ? updateMenu({ variables: { _id: shopMenu._id, subMenus } })
      : createMenu({ variables: { shopId, subMenus } });

    run
      .then(() => {
        setSaving(false);
        setDirty(false);
        setActionResult(`${words.menu} saved.`);
        refetch();
      })
      .catch((err) => {
        setSaving(false);
        setActionError(readableError(err));
      });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superShopsPage">
        <main className="main">
          <PageHeader title={words.menu} />
          <Skeleton height={120} />
          <Skeleton height={120} />
        </main>
      </div>
    );
  }

  if (error) return <ErrorPage />;

  const assignedIds = new Set(groups.reduce((acc, g) => acc.concat(g.productIds), []));
  const unassigned = allProducts.filter((p) => !assignedIds.has(p._id));

  const searchLower = productSearch.trim().toLowerCase();
  const visibleProducts = searchLower
    ? allProducts.filter((p) => (p.title || "").toLowerCase().indexOf(searchLower) !== -1)
    : allProducts;

  return (
    <div className="page superShopsPage">
      <main className="main">
        <PageHeader
          title={words.menu}
          count={groups.length}
          description={
            shopTitle
              ? `How ${shopTitle} groups what it sells. Customers browse these.`
              : "How this shop groups what it sells. Customers browse these."
          }
          actions={
            <React.Fragment>
              <Button variant="secondary" icon={<AddIcon fontSize="small" />} onClick={openAdd}>
                Add {words.menuItem.toLowerCase()}
              </Button>
              <Button variant="primary" onClick={onSave} loading={saving} disabled={!dirty}>
                {dirty ? "Save changes" : "Saved"}
              </Button>
            </React.Fragment>
          }
        />

        {dirty && (
          <div className="dsFormMessage warning" role="status">
            You have unsaved changes to the {words.menu.toLowerCase()}.
          </div>
        )}
        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}
        {actionResult && (
          <div className="dsFormMessage success" role="status">
            {actionResult}
          </div>
        )}

        {unassigned.length > 0 && (
          <div className="dsFormMessage info" role="status">
            <span>
              {unassigned.length} {unassigned.length === 1 ? "item is" : "items are"} not in any{" "}
              {words.menuItem.toLowerCase()} yet, so customers cannot find{" "}
              {unassigned.length === 1 ? "it" : "them"}.
            </span>
          </div>
        )}

        {groups.length === 0 ? (
          <EmptyState
            icon={<MenuBookIcon />}
            title={`No ${words.menu.toLowerCase()} yet`}
            message={`Add a ${words.menuItem.toLowerCase()} to start grouping what this shop sells.`}
            action={
              <Button variant="primary" icon={<AddIcon fontSize="small" />} onClick={openAdd}>
                Add {words.menuItem.toLowerCase()}
              </Button>
            }
          />
        ) : (
          groups.map((group, index) => {
            const items = allProducts.filter((p) => group.productIds.includes(p._id));
            return (
              <Panel
                key={`${group.name}-${index}`}
                title={group.name}
                subtitle={`${group.productIds.length} item${group.productIds.length === 1 ? "" : "s"}`}
                actions={
                  <React.Fragment>
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() => move(index, 1)}
                      disabled={index === groups.length - 1}
                      title="Move down"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() => openRename(index)}
                      title="Rename"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() => setDeleteIndex(index)}
                      title="Remove"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </React.Fragment>
                }
              >
                <div className="superShopImages">
                  {items.length === 0 ? (
                    <span className="superShopMuted">Nothing in here yet.</span>
                  ) : (
                    items.map((product) => (
                      <span className="superShopTag" key={product._id}>
                        {product.title}
                      </span>
                    ))
                  )}
                </div>
                <div className="superShopFooter">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      (window.location.href = `${endPoint}/superAdmin/${shopName}/${shopId}/addProductInShopMenu/${
                        (shopMenu && shopMenu.subMenus[index] && shopMenu.subMenus[index]._id) || ""
                      }`)
                    }
                    disabled={!shopMenu || !shopMenu.subMenus[index]}
                  >
                    New item here
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setProductsTarget(index);
                      setProductSearch("");
                    }}
                  >
                    Choose items
                  </Button>
                </div>
              </Panel>
            );
          })
        )}
      </main>

      {/* ADD / RENAME */}
      <DialogShell
        open={Boolean(nameTarget)}
        onClose={() => setNameTarget(null)}
        title={
          nameTarget && nameTarget.index === null
            ? `Add ${words.menuItem.toLowerCase()}`
            : `Rename ${words.menuItem.toLowerCase()}`
        }
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setNameTarget(null) },
          { label: "Save", onClick: saveName, variant: "primary" },
        ]}
      >
        <div className="superShopForm">
          <Field label="Name" htmlFor="groupName" error={nameError} required>
            <input
              id="groupName"
              className="dsInput"
              type="text"
              value={nameValue}
              autoFocus
              onChange={(e) => setNameValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.charCode === 13) {
                  e.preventDefault();
                  saveName();
                }
              }}
            />
          </Field>
        </div>
      </DialogShell>

      {/* CHOOSE PRODUCTS */}
      <DialogShell
        open={productsTarget !== null}
        onClose={() => setProductsTarget(null)}
        title="Choose items"
        subtitle={productsTarget !== null && groups[productsTarget] ? groups[productsTarget].name : ""}
        size="lg"
        actions={[{ label: "Done", onClick: () => setProductsTarget(null), variant: "primary" }]}
      >
        <div className="superShopDetails">
          <SearchField
            placeholder="Search items by title..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
          <div className="superShopPicker">
            {visibleProducts.length === 0 ? (
              <span className="superShopMuted">No items match that search.</span>
            ) : (
              visibleProducts.map((product) => {
                const group = productsTarget !== null ? groups[productsTarget] : null;
                const selected = group && group.productIds.includes(product._id);
                const elsewhere =
                  !selected &&
                  groups.some((g, i) => i !== productsTarget && g.productIds.includes(product._id));
                const photo = assetUrl((product.photoUrl || [])[0]);
                return (
                  <label className={`dsOptionCard ${selected ? "isSelected" : ""}`} key={product._id}>
                    <input
                      type="checkbox"
                      checked={Boolean(selected)}
                      onChange={() => toggleProduct(productsTarget, product._id)}
                    />
                    <span className="superShopLogo">
                      {photo ? <img src={photo} alt="" /> : <ImageIcon fontSize="small" />}
                    </span>
                    <span className="dsOptionCardText">
                      <strong>{product.title}</strong>
                      <span>
                        ${Number(product.price || 0).toFixed(2)}
                        {elsewhere ? " · already in another group" : ""}
                      </span>
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </DialogShell>

      {/* REMOVE GROUP */}
      <DialogShell
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        title={`Remove ${words.menuItem.toLowerCase()}`}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteIndex(null) },
          { label: "Remove", onClick: removeGroup, variant: "danger" },
        ]}
      >
        <div className="superShopConfirm">
          {deleteIndex !== null && groups[deleteIndex]
            ? `"${groups[deleteIndex].name}" will be removed from the ${words.menu.toLowerCase()} when you save. Its items are not deleted — they just stop being grouped.`
            : ""}
        </div>
      </DialogShell>
    </div>
  );
};

export default SuperAddShopMenu;
